/**
 * Talla parser utility.
 *
 * SKUs suelen venir así:
 *   PLAYERA-DARKAR-CH   → talla "CH"
 *   TENNIS-MODELO-22    → talla "22"
 *   PELUCHE-BOB         → sin talla
 *
 * Regla:
 *  - Tomamos el ÚLTIMO segmento (después del último "-").
 *  - Si matchea una talla conocida (whitelist), la extraemos.
 *  - Si no, el SKU no tiene talla y se muestra tal cual.
 */

// Whitelist letra (orden REAL de tamaño)
export const LETTER_SIZES = ["CH", "M", "G", "XG", "XXG", "XXXG"];
const LETTER_SIZES_UPPER = new Set(LETTER_SIZES.map((s) => s.toUpperCase()));

// Rango numérico razonable (calzado, ropa numérica)
const NUM_MIN = 15;
const NUM_MAX = 45;

/**
 * Devuelve { talla: "CH"|"22"|null, base: "PLAYERA-DARKAR" }
 */
export function parseTalla(sku) {
  if (!sku || typeof sku !== "string") return { talla: null, base: sku || "" };
  const s = sku.trim();
  const idx = s.lastIndexOf("-");
  const tail = idx === -1 ? s.trim().toUpperCase() : s.slice(idx + 1).trim().toUpperCase();
  if (!tail) return { talla: null, base: s };

  // Letra?
  if (LETTER_SIZES_UPPER.has(tail)) {
    return { talla: tail, base: idx === -1 ? "" : s.slice(0, idx) };
  }
  // Numérica?
  if (/^\d{1,3}$/.test(tail)) {
    const n = parseInt(tail, 10);
    if (n >= NUM_MIN && n <= NUM_MAX) {
      return { talla: tail, base: idx === -1 ? "" : s.slice(0, idx) };
    }
  }
  return { talla: null, base: s };
}

/**
 * Intenta obtener la talla desde múltiples fuentes.
 * Prueba el SKU primero, y si no matchea intenta con el nombre/label de la variante.
 * Retorna solo el string de talla ("CH", "22", ...) o null.
 */
export function inferTalla(sku, label) {
  const fromSku = parseTalla(sku).talla;
  if (fromSku) return fromSku;
  if (label) {
    const fromLabel = parseTalla(label).talla;
    if (fromLabel) return fromLabel;
  }
  return null;
}

/**
 * Devuelve un score numérico para ordenar tallas correctamente.
 * Letras: CH=0, M=1, G=2, XG=3, XXG=4, XXXG=5
 * Números: se convierten directo (22 → 22)
 * SKUs sin talla: van al final (Infinity).
 */
export function tallaOrder(talla) {
  if (!talla) return Number.POSITIVE_INFINITY;
  const t = String(talla).toUpperCase();
  const letterIdx = LETTER_SIZES.indexOf(t);
  if (letterIdx !== -1) return letterIdx;
  const n = parseInt(t, 10);
  if (Number.isFinite(n)) return 1000 + n; // letras primero, luego numéricas
  return Number.POSITIVE_INFINITY;
}

/**
 * Ordena una lista de rows (o SKUs) por su talla.
 * Uso: rows.sort((a, b) => compareBySku(a.sku, b.sku))
 */
export function compareBySku(skuA, skuB) {
  const pa = parseTalla(skuA);
  const pb = parseTalla(skuB);
  // Si ambos tienen la misma base, ordenar por talla real
  if (pa.base === pb.base) {
    const oa = tallaOrder(pa.talla);
    const ob = tallaOrder(pb.talla);
    if (oa !== ob) return oa - ob;
  }
  // Fallback alfabético del SKU completo
  return String(skuA || "").localeCompare(String(skuB || ""));
}

/**
 * Ordena rows con SKU + label. Prueba a inferir talla desde ambos campos.
 */
export function compareByRow(a, b) {
  const ta = inferTalla(a.sku, a.label);
  const tb = inferTalla(b.sku, b.label);
  const oa = tallaOrder(ta);
  const ob = tallaOrder(tb);
  if (oa !== ob) return oa - ob;
  return String(a.sku || "").localeCompare(String(b.sku || ""));
}
